import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Helmet } from "../components/Head";
import ChatChart from "../components/ChatChart";
import { useAuth } from "../context/AuthContext";
import { createAnalysisJob, getAnalysisJob } from "../services/analysisApi";
import { getConversation, listConversations, streamChatMessage } from "../services/chatApi";
import "./style/chatV2.css";

// Chatbot v2: odpovědi z interních dat FinPilot (registr firem, oborová statistika, ocenění, analýzy výkazů).
// Historie je na serveru u účtu. Odpověď přichází průběžně (SSE), grafy z bloků ```graf```, návrhy jako tlačítka.

const SUGGESTIONS = [
  "Jak si stojí firma ELMONT, spol. s r.o. ve srovnání s oborem?",
  "Kolik orientačně stojí firma s IČO 26232111 a proč?",
  "Které sekce ekonomiky rostly za 6 let nejrychleji?",
  "Největší firmy ve výrobě farmaceutických přípravků",
  "Jak se vyvíjela EBITDA marže ve stavebnictví?",
  "Chci za pár let prodat firmu. Na co se připravit?",
];

const TOOL_STEPS = {
  najdi_firmu: "Hledám firmu v registru",
  firma_detail: "Načítám čísla firmy",
  konkurenti: "Hledám konkurenty",
  obor: "Načítám statistiku oboru",
  zebricek_oboru: "Sestavuji žebříček oborů",
  vyvoj_oboru: "Načítám vývoj oboru",
  firmy_v_oboru: "Hledám firmy v oboru",
  porovnej_firmy: "Porovnávám firmy",
  clanky: "Hledám v článcích",
  moje_analyzy: "Načítám vaše analýzy",
  aktualni_analyza: "Čtu analýzu výkazů",
  kontakt: "Připravuji kontakt",
};

const LAST_KEY = "fp_chat_last";
const POLL_MS = 3000;
const MAX_TOTAL = 45 * 1024 * 1024;
const SUMMARY_PROMPT =
  "Shrň mi prosím výsledky analýzy nahraných výkazů: hodnotu firmy, silné a slabé stránky proti oboru a co by hodnotu zvýšilo.";

function storage(fn) {
  try {
    return fn(window.localStorage);
  } catch (e) {
    return null;
  }
}

// během streamování schovat rozepsaný řádek NAVRHY
const visibleText = (t) => (t || "").replace(/\n*\s*NAVRHY:[\s\S]*$/, "");
const isPdf = (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");

const mdComponents = {
  a: ({ href, children }) => {
    const external = /^https?:\/\//.test(href || "") && !(href || "").includes("finpilot.cz");
    return (
      <a href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}>
        {children}
      </a>
    );
  },
  pre: ({ node, children }) => {
    const code = node?.children?.[0];
    const cls = code?.properties?.className || [];
    if (Array.isArray(cls) && cls.includes("language-graf")) {
      const raw = (code.children || []).map((c) => c.value || "").join("");
      try {
        return <ChatChart spec={JSON.parse(raw)} />;
      } catch (e) {
        return <div className="cb2-chart cb2-chart--loading">Připravuji graf…</div>;
      }
    }
    return <pre>{children}</pre>;
  },
};

const Icon = {
  clip: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.4 11.1l-8.5 8.5a5.5 5.5 0 01-7.8-7.8l8.5-8.5a3.7 3.7 0 015.2 5.2l-8.5 8.5a1.8 1.8 0 01-2.6-2.6l7.8-7.8" />
    </svg>
  ),
  send: (
    <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 2L11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  ),
  copy: (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  ),
};

export default function ChatbotPage() {
  const { reload } = useAuth();
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState("");
  const [error, setError] = useState("");
  const [showList, setShowList] = useState(false);
  const [job, setJob] = useState(null);
  const [pending, setPending] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [copied, setCopied] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const dragDepth = useRef(0);
  const convRef = useRef(null);
  convRef.current = conversationId;

  const refreshList = useCallback(() => {
    listConversations()
      .then((d) => setConversations(d.conversations || []))
      .catch(() => {});
  }, []);

  const openConversation = useCallback(async (id) => {
    setError("");
    setShowList(false);
    try {
      const d = await getConversation(id);
      setConversationId(d.id);
      setMessages(d.messages || []);
      setJob(d.job || null);
      storage((s) => s.setItem(LAST_KEY, d.id));
    } catch (e) {
      storage((s) => s.removeItem(LAST_KEY));
      setConversationId(null);
      setMessages([]);
      setJob(null);
    }
  }, []);

  useEffect(() => {
    refreshList();
    const last = storage((s) => s.getItem(LAST_KEY));
    if (last) openConversation(last);
  }, [refreshList, openConversation]);

  useEffect(() => {
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, sending, pending, step]);

  useEffect(() => () => clearTimeout(pollRef.current), []);

  const newConversation = () => {
    setConversationId(null);
    setMessages([]);
    setJob(null);
    setError("");
    setShowList(false);
    storage((s) => s.removeItem(LAST_KEY));
    inputRef.current?.focus();
  };

  const send = async (value, jobId) => {
    const q = (value ?? text).trim();
    if (!q || sending) return;
    setError("");
    setText("");
    setSending(true);
    setStep("Přemýšlím");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "", streaming: true }]);
    const patchLast = (fn) =>
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = fn(copy[copy.length - 1]);
        return copy;
      });
    try {
      await streamChatMessage(q, convRef.current, jobId, (ev) => {
        if (ev.type === "start") {
          if (ev.conversation_id !== convRef.current) {
            setConversationId(ev.conversation_id);
            convRef.current = ev.conversation_id;
            storage((s) => s.setItem(LAST_KEY, ev.conversation_id));
          }
          if (ev.job) setJob(ev.job);
        } else if (ev.type === "tool") {
          setStep(TOOL_STEPS[ev.name] || "Hledám v datech");
        } else if (ev.type === "delta") {
          setStep("");
          patchLast((msg) => ({ ...msg, content: msg.content + ev.text }));
        } else if (ev.type === "done") {
          patchLast(() => ({ role: "assistant", content: ev.answer, suggestions: ev.suggestions || [] }));
        } else if (ev.type === "error") {
          throw new Error(ev.detail);
        }
      });
      refreshList();
      if (reload) reload();
    } catch (e) {
      setMessages((m) => m.slice(0, -2));
      setText(q);
      setError(e.message || "Odpověď se nepodařilo načíst.");
    } finally {
      setSending(false);
      setStep("");
      inputRef.current?.focus();
    }
  };

  const pollJob = async (id) => {
    clearTimeout(pollRef.current);
    try {
      const d = await getAnalysisJob(id);
      if (d.status === "queued" || d.status === "processing") {
        setPending({ id, step: d.step });
        pollRef.current = setTimeout(() => pollJob(id), POLL_MS);
        return;
      }
      setPending(null);
      if (d.status === "ready" || d.status === "needs_review") {
        setJob({ id: d.id, firma: d.result?.company?.name || d.company_name, rok: d.report_year, ico: d.ico });
        send(SUMMARY_PROMPT, d.id);
      } else {
        if (d.credits_refunded && reload) reload();
        setError(d.message || "Výkazy se nepodařilo zpracovat. Kredity byly vráceny.");
      }
    } catch (e) {
      setPending(null);
      setError(e.message);
    }
  };

  const onUpload = async (list) => {
    if (fileRef.current) fileRef.current.value = "";
    const all = Array.from(list || []);
    const files = all.filter(isPdf).slice(0, 6);
    if (!files.length) {
      if (all.length) setError("Nahrát lze jen PDF (rozvaha a výkaz zisku a ztráty).");
      return;
    }
    if (files.reduce((sum, f) => sum + f.size, 0) > MAX_TOTAL) {
      setError("Soubory mají dohromady víc než 45 MB. Nahrajte prosím jen rozvahu a výkaz zisku a ztráty.");
      return;
    }
    if (pending || sending) return;
    setError("");
    try {
      const created = await createAnalysisJob({ files });
      if (reload) reload();
      setPending({ id: created.id, step: "nahráno, začínám zpracovávat" });
      pollJob(created.id);
    } catch (e) {
      setError(e.message);
    }
  };

  // drag & drop PDF do okna chatu
  const onDragEnter = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragOver = (e) => {
    if (dragging) e.preventDefault();
  };
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) setDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    onUpload(e.dataTransfer?.files);
  };

  const onAttach = () => fileRef.current?.click();

  const copy = async (i, content) => {
    try {
      await navigator.clipboard.writeText(visibleText(content).replace(/```graf[\s\S]*?```/g, "").trim());
      setCopied(i);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) {
      /* schránka není dostupná */
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const lastBot = messages.map((m) => m.role).lastIndexOf("assistant");
  const busy = sending || !!pending;

  return (
    <div className="cb2">
      <Helmet>
        <title>AI finanční poradce pro firmy | FinPilot</title>
        <meta
          name="description"
          content="Zeptejte se na hodnotu firmy, srovnání s oborem, konkurenty nebo přípravu na prodej. Odpovědi z dat o českých firmách."
        />
        <link rel="canonical" href="https://finpilot.cz/chatbot" />
      </Helmet>

      <header className="cb2-head">
        <h1>FinPilot chat</h1>
        <p className="cb2-muted">
          Hodnota firmy, srovnání s oborem, konkurenti, vývoj oborů a příprava na prodej. Čísla pocházejí z dat FinPilot, ne z
          odhadu modelu.
        </p>
      </header>

      <section
        className={`cb2-chat${dragging ? " is-dragging" : ""}`}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div className="cb2-chat__top">
          {job ? (
            <span className="cb2-job">
              Rozhovor o výkazech: <strong>{job.firma || "firma"}</strong>
              {job.rok ? ` · ${job.rok}` : ""}
              <Link to={`/app/financni-analyza?job=${job.id}`}>celý report</Link>
            </span>
          ) : (
            <span className="cb2-muted cb2-small">Nový rozhovor</span>
          )}
          <button type="button" className="cb2-icon-btn" onClick={newConversation} title="Nový chat" aria-label="Nový chat">
            {Icon.plus}
          </button>
        </div>

        <div className="cb2-messages" ref={listRef} aria-live="polite">
          {messages.length === 0 && !pending && (
            <div className="cb2-empty">
              <p className="cb2-empty__lead">Na co se chcete zeptat?</p>
              <div className="cb2-suggest">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" className="cb2-chip" onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
              </div>
              <p className="cb2-muted cb2-small">
                Můžete sem také přetáhnout rozvahu a výkaz zisku a ztráty (PDF, i sken). FinPilot je zanalyzuje a pak se
                můžete ptát na výsledky. Analýza stojí 5 kreditů.
              </p>
            </div>
          )}

          {messages.map((m, i) =>
            m.role === "user" ? (
              <div key={i} className="cb2-msg cb2-msg--user">
                <div className="cb2-bubble">{m.content}</div>
              </div>
            ) : (
              <div key={i} className="cb2-msg cb2-msg--bot">
                <div className="cb2-bubble cb2-md">
                  {m.streaming && !m.content ? (
                    <span className="cb2-typing">
                      <span />
                      <span />
                      <span />
                      <em>{step || "Přemýšlím"}…</em>
                    </span>
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                      {visibleText(m.content)}
                    </ReactMarkdown>
                  )}
                  {!m.streaming && m.content && (
                    <div className="cb2-msg__actions">
                      <button type="button" onClick={() => copy(i, m.content)} title="Kopírovat odpověď">
                        {Icon.copy}
                        {copied === i ? "Zkopírováno" : "Kopírovat"}
                      </button>
                    </div>
                  )}
                </div>
                {i === lastBot && !busy && m.suggestions?.length > 0 && (
                  <div className="cb2-followups">
                    {m.suggestions.map((s) => (
                      <button key={s} type="button" className="cb2-chip cb2-chip--sm" onClick={() => send(s)}>
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          )}

          {pending && (
            <div className="cb2-msg cb2-msg--bot">
              <div className="cb2-bubble">
                <span className="cb2-typing">
                  <span />
                  <span />
                  <span />
                  <em>Zpracovávám výkazy: {pending.step || "pracuji"}… (u skenů i několik minut)</em>
                </span>
              </div>
            </div>
          )}
        </div>

        {dragging && (
          <div className="cb2-dropzone">
            <div>
              {Icon.clip}
              <strong>Pusťte PDF výkazy sem</strong>
              <span>rozvaha a výkaz zisku a ztráty, i sken · 5 kreditů</span>
            </div>
          </div>
        )}

        {error && <div className="cb2-error">{error}</div>}

        <form
          className="cb2-input"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <div className="cb2-tipwrap">
            <button type="button" className="cb2-round cb2-attach" onClick={onAttach} disabled={busy} aria-label="Nahrát výkazy PDF">
              {Icon.clip}
            </button>
            <div className="cb2-tip" role="tooltip">
              <strong>Nahrát výkazy PDF</strong>
              Rozvaha a výkaz zisku a ztráty (i sken). Soubory můžete také přetáhnout do chatu. Analýza stojí 5 kreditů.
            </div>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" multiple hidden onChange={(e) => onUpload(e.target.files)} />
          <textarea
            ref={inputRef}
            rows={2}
            maxLength={2000}
            value={text}
            placeholder="Napište dotaz, např. „Kdo jsou konkurenti firmy s IČO 15057798?“"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            disabled={busy}
          />
          <button type="submit" className="cb2-round cb2-send" disabled={busy || !text.trim()} aria-label="Odeslat" title="Odeslat">
            {Icon.send}
          </button>
        </form>
      </section>

      <div className="cb2-bottom">
        <div className="cb2-bottom__actions">
          <button type="button" className="cb2-btn" onClick={() => setShowList((v) => !v)} aria-expanded={showList}>
            Historie{conversations.length ? ` (${conversations.length})` : ""}
          </button>
          <button type="button" className="cb2-btn" onClick={newConversation}>
            Nový chat
          </button>
          {messages.length > 0 && (
            <button type="button" className="cb2-btn" onClick={() => window.print()}>
              Uložit jako PDF
            </button>
          )}
        </div>
        <span className="cb2-muted cb2-small">1 zpráva = 1 kredit, s daty o firmách a oborech 2 kredity</span>
      </div>

      {showList && (
        <div className="cb2-history">
          {conversations.length === 0 && <p className="cb2-muted">Zatím žádné konverzace.</p>}
          {conversations.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`cb2-history__item${c.id === conversationId ? " is-active" : ""}`}
              onClick={() => openConversation(c.id)}
            >
              <span className="cb2-history__title">{c.title}</span>
              {c.last_at && <span className="cb2-history__date">{new Date(c.last_at).toLocaleDateString("cs-CZ")}</span>}
            </button>
          ))}
        </div>
      )}

      <p className="cb2-muted cb2-small cb2-foot">
        Orientační informace, ne znalecký posudek ani daňová rada. Konkrétní transakci probereme osobně:{" "}
        <a href="mailto:info@finpilot.cz">info@finpilot.cz</a>
      </p>
    </div>
  );
}
